import React, { FC } from "react";
import Selector from "../selectors/Selector";
import { IModules, ILines } from "../../../models/IUser";
import {
  CABINET_MODEL_LABELS,
  cabinetModelOf,
  DeviceTypeConfig,
} from "./deviceTypeConfig";

type FieldKey = keyof DeviceTypeConfig["showFields"];

interface FieldDef {
  key: FieldKey;
  label: string;
  getValue: (
    module: IModules,
    editedModule: IModules | null,
    modules: IModules[],
  ) => string | number;
  dimension?: string
}

const FIELD_DEFS: FieldDef[] = [
  {
    key: "parent_module_id",
    label: "ММБ:",
    getValue: (_, ed, modules) => {
      const parentId = ed?.parent_module_id;
      if (parentId == null) return "";
      return modules.find((m) => m.id === parentId)?.charge_id || "";
    },
  },
  { key: "serial", label: "серийный номер:", getValue: (_, ed) => ed?.serial || "" },
  { key: "espVersion", label: "версия ESP:", getValue: (m) => m.ESP || "" },
  { key: "stmVersion", label: "версия STM:", getValue: (m) => m.STM || "" },
  { key: "limit", label: "лимит тока на фазу", getValue: (_, ed) => ed?.limit || "", dimension: 'A' },
  { key: "primary_current", label: "первичный ток ТТ", getValue: (_, ed) => ed?.primary_current || "", dimension: 'A' },
  { key: "ws_mmb_url", label: "адрес ws-сервера:", getValue: (_, ed) => ed?.ws_mmb_url || "", dimension: 'IP' },
  { key: "ws_mmb_port", label: "порт:", getValue: (_, ed) => ed?.ws_mmb_port || ""},
  // { key: "meter_number", label: "номер счетчика:", getValue: (_, ed) => ed?.meter_number || "" },
  // { key: "meter_address", label: "адрес счетчика:", getValue: (_, ed) => ed?.meter_address || "" },
  // { key: "last_reading", label: "показания счетчика:", getValue: (_, ed) => ed?.last_reading || "" },
  { key: "timer", label: "период отправки:", getValue: (_, ed) => ed?.timer || "", dimension: 'сек' },
  { key: "max_calc_load", label: "максимальная расчетная нагрузка:", getValue: (_, ed) => ed?.max_calc_load || "", dimension: 'A' }

];

function getReadonlyFields(config: DeviceTypeConfig): FieldDef[] {
  return FIELD_DEFS.filter(
    (f) => config.showFields[f.key] && config.editableFields[f.key] !== true,
  );
}

function getEditableFields(config: DeviceTypeConfig): FieldDef[] {
  return FIELD_DEFS.filter(
    (f) => config.showFields[f.key] && config.editableFields[f.key] === true,
  );
}

export function getFieldLabel(key: string): string {
  const def = FIELD_DEFS.find((f) => f.key === key);
  return def ? def.label.replace(/:\s*$/, "").trim() : key;
}

interface PopupModuleInfoProps {
  module: IModules;
  editedModule: IModules | null;
  modules: IModules[];
  lines: ILines[];
  limit: string | number;
  changedModuleFields: Set<string>;
  fieldErrors?: Record<string, string>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLineChange: (lineId: number) => void;
  config: DeviceTypeConfig;
  /** `G.2`: модель шкафа меняет только root; остальным она видна текстом. */
  isRoot?: boolean;
}

export const PopupModuleInfo: FC<PopupModuleInfoProps> = ({
  module,
  editedModule,
  modules,
  lines,
  limit,
  changedModuleFields,
  fieldErrors,
  onChange,
  onLineChange,
  config,
  isRoot = false,
}) => {
  const readonlyFields = getReadonlyFields(config);
  const editableFields = getEditableFields(config);

  return (
    <div className="gufi_container">
      <div className="gufi_block gufi_big-block">
        <div className="gufi_big-block_container">
          <div className="gufi_big-block_serial">{module.charge_id}</div>
          <div className="gufi_big-block_IDPLant">
            <div>{module["Plant.name"] || ""}</div>
          </div>
        </div>
        {readonlyFields.length > 0 && (
          <div className="gufi_big-block_readonly">
            {readonlyFields.map((f) => (
              <div key={f.key} className="gufi_big-block_readonly-row">
                <span>{f.label}</span>
                <span>{f.getValue(module, editedModule, modules)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {editableFields.length > 0 && (
        <div className="gufi_block gufi_block--wide">
          <div className="gufi_block_title">МОДУЛЬ:</div>
          <div className="gufi_block_param_module">
            {editableFields.map((f) => (
              <InfoRow
                key={f.key}
                label={f.label}
                name={f.key}
                value={f.getValue(module, editedModule, modules)}
                dimension={f.dimension}
                isChanged={changedModuleFields.has(f.key)}
                error={fieldErrors?.[f.key]}
                onChange={onChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* `G.2` (решение РП 22.10): модель шкафа. Меняет только root — на сервере
          то же правило (403 остальным). Остальным модель видна текстом: скрывать
          её совсем нельзя, по ней объясняется, почему станций девять, а не
          пятнадцать. */}
      {config.tableMode === "stations" && module.device_type !== "smb" ? (
        <div className="gufi_block">
          <div className="gufi_block_title">МОДЕЛЬ ШКАФА:</div>
          {isRoot ? (
            <select
              aria-label="Модель шкафа"
              name="cabinet_model"
              value={String(cabinetModelOf((editedModule || module).cabinet_model))}
              onChange={onChange as unknown as (e: React.ChangeEvent<HTMLSelectElement>) => void}
            >
              <option value="15">{CABINET_MODEL_LABELS[15]}</option>
              <option value="9">{CABINET_MODEL_LABELS[9]}</option>
            </select>
          ) : (
            <div>{CABINET_MODEL_LABELS[cabinetModelOf(module.cabinet_model)]}</div>
          )}
        </div>
      ) : null}

      <div className="gufi_block">
        <div className="gufi_block_title">ЛИНИЯ:</div>
        <Selector
          options={lines}
          value={module.line_id}
          onChange={onLineChange}
          placeholder=""
          disabled={true}
        />
        <div className="gufi_block_param_line">
          <div><span>лимит тока линии : </span>{limit || 0} А</div>
          <div><span>ток ФАЗА-А : </span>{module.value_A || 0} А</div>
          <div><span>ток ФАЗА-B : </span>{module.value_B || 0} А</div>
          <div><span>ток ФАЗА-C : </span>{module.value_C || 0} А</div>
        </div>
      </div>
    </div>
  );
};

interface InfoRowProps {
  label: string;
  name: string;
  value: string | number;
  dimension?: string;
  isChanged?: boolean;
  error?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const InfoRow: FC<InfoRowProps> = ({
  label,
  name,
  value,
  dimension,
  isChanged,
  error,
  onChange,
}) => (
  <div className="gufi_block_param_row">
    <span className="gufi_block_param_name">{label}</span>
    <div className="gufi_block_param_input-wrap">
      <input
        className={[
          isChanged ? "field-changed" : "",
          error ? "field-error" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        type="text"
        name={name}
        value={value}
        onChange={onChange}
      />
      {dimension && <span className="gufi_block_param_dimension">{dimension}</span>}
      {error && <span className="gufi_block_param_error">{error}</span>}
    </div>
  </div>
);
